import React from 'react';
import { StaleChunkError } from './stale-chunk-error';
import { handleWarning } from '@tectonics/observability';
import { wait, toError, getRetryDelay } from './retry';

type LazyModule = Awaited<ReturnType<Parameters<typeof React.lazy>[0]>>;

export const SAFE_LAZY_CONFIG = {
  MAX_RETRIES: 2,
  RETRY_DELAY_MS: 1000,
  RELOAD_STORAGE_KEY: 'safeLazy:reloadedAt',
  RELOAD_COOLDOWN_MS: 10000,
};

function hasValidDefault(module: unknown): module is LazyModule {
  return typeof module === 'object'
    && module !== null
    && 'default' in module
    && module.default !== undefined;
}

function shouldReload(): boolean {
  try {
    const ts = sessionStorage.getItem(SAFE_LAZY_CONFIG.RELOAD_STORAGE_KEY);
    if (!ts) return true;
    return Date.now() - Number(ts) > SAFE_LAZY_CONFIG.RELOAD_COOLDOWN_MS;
  } catch (_) {
    return false;
  }
}

function markReload(): boolean {
  try {
    sessionStorage.setItem(SAFE_LAZY_CONFIG.RELOAD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch (_) {
    return false;
  }
}

export const safeLazy: typeof React.lazy = importFn => {
  // eslint-disable-next-line no-restricted-properties
  return React.lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= SAFE_LAZY_CONFIG.MAX_RETRIES; attempt++) {
      try {
        const mod = await importFn();

        if (hasValidDefault(mod)) return mod;

        // Module resolved but has no default export (stale chunk).
        // Browser caches ESM modules by URL, so retrying returns the same
        // broken result. Skip remaining retries and go straight to reload.
        lastError = new StaleChunkError(
          'Lazy chunk resolved without a default export (stale chunk after deploy)',
        );
        break;
      } catch (e) {
        lastError = toError(e);
        if (attempt > 0) {
          handleWarning(`safeLazy: retry attempt ${attempt} failed – ${lastError.message}`);
        }
      }

      if (attempt < SAFE_LAZY_CONFIG.MAX_RETRIES) {
        await wait(getRetryDelay(SAFE_LAZY_CONFIG.RETRY_DELAY_MS, attempt));
      }
    }

    if (shouldReload() && markReload()) {
      const reason =
        lastError instanceof StaleChunkError ? 'stale chunk' : 'failed chunk load';
      handleWarning(`safeLazy: reloading page due to ${reason} – ${lastError?.message ?? 'unknown error'}`);
      window.location.reload();
      // Never resolves – Suspense keeps showing its fallback until the page reloads.
      return new Promise(() => {});
    }

    throw lastError || new StaleChunkError(
      'Lazy chunk resolved without a default export (stale chunk after deploy)',
    );
  });
};
